import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FiCheckCircle,
  FiXCircle,
  FiAlertTriangle,
  FiInfo,
  FiLoader,
} from "react-icons/fi";

/* ── Custom toast bodies ── */
function ToastBody({ icon: Icon, title, message, iconCls }) {
  return (
    <div className="flex items-start gap-3 py-0.5">
      <div className={`mt-0.5 shrink-0 ${iconCls}`}>
        <Icon size={18} />
      </div>
      <div className="flex flex-col gap-0.5 min-w-0">
        {title && (
          <p className="text-[13px] font-semibold text-slate-800 leading-tight">
            {title}
          </p>
        )}
        <p className="text-[12px] text-slate-500 leading-snug break-words">
          {message}
        </p>
      </div>
    </div>
  );
}

const BASE = {
  position: "top-right",
  autoClose: 4000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
};

/**
 * Extracts a clean, human-readable error message from any error type:
 * - Axios Error objects with response.data.errors / message / error
 * - Validation error dictionaries { field: ["Error 1", "Error 2"] }
 * - Error instances, arrays of strings, or plain strings
 */
export function parseErrorMessage(input, fallback = "An unexpected error occurred.") {
  if (!input) return fallback;

  // 1. If it's a direct string
  if (typeof input === "string") {
    const trimmed = input.trim();
    if (trimmed.startsWith("<!DOCTYPE") || trimmed.startsWith("<html")) {
      return fallback;
    }
    return trimmed || fallback;
  }

  // 2. If it's an Axios or JavaScript Error object
  if (input instanceof Error || input.isAxiosError || input.response || input.request) {
    const data = input.response?.data;
    if (data) {
      // (a) Laravel validation errors: data.errors = { field: ["msg"] }
      if (data.errors && typeof data.errors === "object") {
        const errorList = Object.values(data.errors).flat().filter(Boolean);
        if (errorList.length > 0) {
          return errorList.join(" • ");
        }
      }

      // (b) data.message
      if (data.message) {
        if (typeof data.message === "string") {
          const trimmed = data.message.trim();
          if (!trimmed.startsWith("<!DOCTYPE") && !trimmed.startsWith("<html")) {
            return trimmed;
          }
        } else if (typeof data.message === "object") {
          const errorList = Object.values(data.message).flat().filter(Boolean);
          if (errorList.length > 0) {
            return errorList.join(" • ");
          }
        }
      }

      // (c) data.error
      if (data.error) {
        if (typeof data.error === "string") {
          return data.error;
        } else if (typeof data.error === "object") {
          const errorList = Object.values(data.error).flat().filter(Boolean);
          if (errorList.length > 0) {
            return errorList.join(" • ");
          }
        }
      }

      // (d) Direct string response body
      if (typeof data === "string" && !data.trim().startsWith("<")) {
        return data.trim();
      }
    }

    // Axios client-side or network errors
    if (input.message && typeof input.message === "string") {
      const lower = input.message.toLowerCase();
      if (lower.includes("network error")) {
        return "Network connection error. Please check your internet connection.";
      }
      if (lower.includes("timeout")) {
        return "Request timed out. Please try again.";
      }
      if (input.message !== "Error" && !input.message.startsWith("Request failed with status")) {
        return input.message;
      }
    }

    return fallback;
  }

  // 3. If it's a plain object (e.g. { email: ["Invalid email"] } or { status: false, message: "..." })
  if (typeof input === "object") {
    if (input.errors && typeof input.errors === "object") {
      const errorList = Object.values(input.errors).flat().filter(Boolean);
      if (errorList.length > 0) return errorList.join(" • ");
    }
    if (input.message && typeof input.message === "string") return input.message;
    if (input.error && typeof input.error === "string") return input.error;

    const errorList = Object.values(input).flat().filter(Boolean);
    if (errorList.length > 0 && typeof errorList[0] === "string") {
      return errorList.join(" • ");
    }
  }

  return fallback;
}

/* ── notify helper — use this everywhere instead of toast.xxx() ── */
export const notify = {
  success: (message, title = "Success") =>
    toast.success(
      <ToastBody
        icon={FiCheckCircle}
        title={title}
        message={parseErrorMessage(message, typeof message === "string" ? message : "Operation completed successfully.")}
        iconCls="text-emerald-500"
      />,
      { ...BASE, className: "!border-l-4 !border-emerald-500" }
    ),

  error: (errOrMessage, fallbackOrTitle = "Error", title = "Error") => {
    let resolvedTitle = title;
    let fallback = "An error occurred.";

    if (typeof fallbackOrTitle === "string") {
      if (fallbackOrTitle === "Error" || fallbackOrTitle === "Warning" || fallbackOrTitle === "Validation Error") {
        resolvedTitle = fallbackOrTitle;
      } else {
        fallback = fallbackOrTitle;
      }
    }

    const message = parseErrorMessage(errOrMessage, fallback);

    return toast.error(
      <ToastBody
        icon={FiXCircle}
        title={resolvedTitle}
        message={message}
        iconCls="text-rose-500"
      />,
      { ...BASE, className: "!border-l-4 !border-rose-500" }
    );
  },

  apiError: (err, fallback = "An error occurred.", title = "Error") =>
    notify.error(err, fallback, title),

  warn: (message, title = "Warning") =>
    toast.warn(
      <ToastBody
        icon={FiAlertTriangle}
        title={title}
        message={parseErrorMessage(message, typeof message === "string" ? message : "Warning")}
        iconCls="text-amber-500"
      />,
      { ...BASE, className: "!border-l-4 !border-amber-500" }
    ),

  info: (message, title = "Info") =>
    toast.info(
      <ToastBody
        icon={FiInfo}
        title={title}
        message={parseErrorMessage(message, typeof message === "string" ? message : "Information")}
        iconCls="text-sky-500"
      />,
      { ...BASE, className: "!border-l-4 !border-sky-500" }
    ),

  loading: (message = "Please wait...") =>
    toast.loading(
      <ToastBody
        icon={FiLoader}
        title="Loading"
        message={message}
        iconCls="text-indigo-500 animate-spin"
      />,
      { ...BASE, autoClose: false, closeOnClick: false }
    ),

  dismiss: (id) => toast.dismiss(id),

  update: (id, type, message, title) => {
    const map = { success: "success", error: "error", warn: "warning", info: "info" };
    toast.update(id, {
      render: (
        <ToastBody
          icon={
            type === "success" ? FiCheckCircle
            : type === "error" ? FiXCircle
            : type === "warn" ? FiAlertTriangle
            : FiInfo
          }
          title={title}
          message={parseErrorMessage(message, typeof message === "string" ? message : "")}
          iconCls={
            type === "success" ? "text-emerald-500"
            : type === "error" ? "text-rose-500"
            : type === "warn" ? "text-amber-500"
            : "text-sky-500"
          }
        />
      ),
      type: map[type] ?? "default",
      isLoading: false,
      autoClose: 4000,
      closeOnClick: true,
    });
  },
};

/* ── Global container — mounted once in App.jsx ── */
export default function Notify() {
  return (
    <ToastContainer
      position="top-right"
      autoClose={4000}
      hideProgressBar={false}
      newestOnTop
      closeOnClick
      pauseOnHover
      draggable
      theme="light"
      toastClassName="!rounded-xl !shadow-lg !shadow-slate-200/60 !border !border-slate-100 !bg-white !p-4 !min-h-0"
      bodyClassName="!p-0 !m-0"
      progressClassName="!h-[3px]"
    />
  );
}
